import React from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
} from "@mui/material";
import { useEffect, useState } from "react";

import LaunchIcon from "@mui/icons-material/Launch";
import BusinessIcon from "@mui/icons-material/Business";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import { useTranslate } from "../components/Translate/TranslateContext";
import { sectionBox } from "../styles/ComponentStyles";
import OptionsMenu from "../components/Options/OptionsMenu";
import Groups from "./Groups";
import Organizations from "./Organizations";
import Farms from "./Farm";
import { fetchFarms } from "../api/farms";

export default function UnifiedDashboard({
    user,
    onLogout,
}) {
    const { translate } =
        useTranslate();
    const [farms, setFarms] = useState([]);
    const normalizeFarms =
        (response) => {
            if (!response) {
                return [];
            }

            if (
                Array.isArray(
                    response?.[0]
                )
            ) {
                return response[0];
            }

            if (
                Array.isArray(
                    response
                )
            ) {
                return response;
            }

            return [];
        };
    useEffect(() => {
        async function load() {
            try {
                const response =
                    await fetchFarms();

                setFarms(
                    normalizeFarms(
                        response
                    )
                );
            } catch (
                error
            ) {
                console.error(
                    error
                );
            }
        }

        load();
    }, []);

    const openExternal =
        (url) => {
            window.open(
                url,
                "_blank",
                "noopener,noreferrer"
            );
        };

    return (
        <Box
            sx={{
                background:
                    "var(--color-bg-primary)",
                minHeight:
                    "100vh",
            }}
        >
            <Box
                sx={{
                    position:
                        "sticky",
                    top: 0,
                    zIndex: 10,
                    backdropFilter:
                        "blur(10px)",
                    backgroundColor:
                        "rgba(255,255,255,0.8)",
                    borderBottom:
                        "1px solid rgba(0,0,0,0.08)",
                    py: 2,
                }}
            >
                <Container
                    maxWidth="xl"
                    sx={{
                        display:
                            "flex",
                        alignItems:
                            "center",
                        gap: { xs: 1.5, sm: 2 }
                    }}
                >
                    <a
                        href="https://tensoai.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        <img
                            src="/tensoLogo.jpg"
                            alt="TensoAI Logo"
                            style={{ width: 32, height: 32 }}
                        />
                    </a>

                    <Typography
                        sx={{
                            fontFamily:
                                "Fraunces",
                            fontSize: {
                                xs: 22,
                                sm: 28
                            },
                            color:
                                "var(--color-text-primary)",
                            whiteSpace: "nowrap"
                        }}
                    >
                        HarvestHub
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <OptionsMenu
                        user={user}
                        onLogout={
                            onLogout
                        }
                    />
                </Container>
            </Box>

            <Container
                maxWidth="xl"
                sx={{ py: { xs: 3, sm: 5 } }}
            >
                <Box
                    sx={{
                        ...sectionBox,
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display:
                                "grid",
                            gridTemplateColumns:
                                {
                                    xs: "1fr",
                                    md: "repeat(2, 1fr)",
                                },
                            gap: 3,
                            mb: 4,
                        }}
                    >
                        <Paper
                            elevation={0}
                            onClick={() =>
                                openExternal(
                                    "https://farmsync.tensoai.com"
                                )
                            }
                            sx={{
                                borderRadius:
                                    4,
                                p: { xs: 3, sm: 5 },
                                cursor:
                                    "pointer",
                                border:
                                    "1px solid rgba(0,0,0,0.08)",
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "space-between",
                                transition:
                                    "0.2s ease",
                                "&:hover":
                                    {
                                        transform:
                                            "translateY(-2px)",
                                        boxShadow:
                                            "0 10px 24px rgba(0,0,0,0.08)",
                                    },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily:
                                        "Fraunces",
                                    fontSize: {
                                        xs: 24,
                                        sm: 32
                                    },
                                    color:
                                        "var(--color-text-primary)",
                                }}
                            >
                                {translate(
                                    "farmsync"
                                )}
                            </Typography>

                            <LaunchIcon />
                        </Paper>

                        <Paper
                            elevation={0}
                            onClick={() =>
                                openExternal(
                                    "https://agrinexus.tensoai.com"
                                )
                            }
                            sx={{
                                borderRadius:
                                    4,
                                p: { xs: 3, sm: 5 },
                                cursor:
                                    "pointer",
                                border:
                                    "1px solid rgba(0,0,0,0.08)",
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "space-between",
                                transition:
                                    "0.2s ease",
                                "&:hover":
                                    {
                                        transform:
                                            "translateY(-2px)",
                                        boxShadow:
                                            "0 10px 24px rgba(0,0,0,0.08)",
                                    },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily:
                                        "Fraunces",
                                    fontSize: {
                                        xs: 24,
                                        sm: 32
                                    },
                                    color:
                                        "var(--color-text-primary)",
                                }}
                            >
                                {translate(
                                    "agrinexus"
                                )}
                            </Typography>

                            <LaunchIcon />
                        </Paper>
                    </Box>

                    <Box
                        sx={{
                            display:
                                "grid",
                            gridTemplateColumns:
                                {
                                    xs: "1fr",
                                    md: "repeat(3, 1fr)",
                                },
                            gap: 3,
                        }}
                    >
                        <Organizations
                            translate={
                                translate
                            }
                            user={user}
                            farms={farms}
                        />

                        <Farms
                            translate={
                                translate
                            }
                            farms={farms}
                        />

                        <Groups
                            user={user}
                        />
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}